/**
 * LitterLivePreview — podgląd na żywo oferty miotu (prawa kolumna kreatora miotu).
 * Styl spójny 1:1 z DogLivePreview / LivePreview Kreatora Hodowli (KROKI | FORMULARZ | PODGLĄD NA ŻYWO).
 */
import { useState } from 'react';
import { Monitor, Smartphone, Heart, Calendar, Users, Tag, PawPrint, Globe, Search, Sparkles, Check } from 'lucide-react';

const STATUS = {
  planned: { label: 'Planowany', className: 'bg-blue-50 text-blue-600' },
  available: { label: 'Dostępny', className: 'bg-emerald-50 text-emerald-600' },
  reservations: { label: 'Rezerwacje', className: 'bg-amber-50 text-amber-600' },
  sold: { label: 'Sprzedany', className: 'bg-zinc-100 text-zinc-500' },
};

export default function LitterLivePreview({ form }) {
  const [mobile, setMobile] = useState(false);
  const st = STATUS[form.status] || STATUS.available;
  const parents = [form.mother_name, form.father_name].filter(Boolean).join(' × ');
  const totalPuppies = (parseInt(form.puppy_count_male) || 0) + (parseInt(form.puppy_count_female) || 0);
  const available = parseInt(form.available_count) || 0;

  return (
    <div data-testid="litter-live-preview">
      {/* Pasek statusu podglądu */}
      <div className="mb-3 px-1">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs uppercase tracking-wider font-semibold text-gray-500 truncate">
              Twoja oferta · podgląd na żywo
            </span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold ring-1 ring-emerald-200/60">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
          </div>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            <button type="button" onClick={() => setMobile(false)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${!mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Desktop">
              <Monitor className="w-4 h-4" />
            </button>
            <button type="button" onClick={() => setMobile(true)} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${mobile ? 'bg-white shadow-sm text-orange-600' : 'text-gray-500'}`} aria-label="Mobile">
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* Ecosystem signals strip — 1:1 z Kreatorem Hodowli */}
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="litter-live-preview-ecosystem-signals">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Globe className="w-3 h-3" /> Widoczne publicznie
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[10px] font-medium ring-1 ring-blue-200/50">
            <Search className="w-3 h-3" /> Gotowe pod Google
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 text-[10px] font-medium ring-1 ring-orange-200/50">
            <Sparkles className="w-3 h-3" /> Indeksowane w wyszukiwarkach
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-medium ring-1 ring-emerald-200/50">
            <Check className="w-3 h-3" /> Mobile-ready
          </span>
        </div>
      </div>

      {/* Karta oferty */}
      <div className={`mx-auto bg-white rounded-3xl border border-zinc-200 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.25)] overflow-hidden transition-all ${mobile ? 'max-w-[320px]' : 'w-full'}`}>
        {/* Hero / zdjęcie główne */}
        <div className="relative aspect-[16/10] bg-gradient-to-br from-orange-100 to-zinc-100">
          {form.cover_image ? (
            <img src={form.cover_image} alt={form.name || 'miot'} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-orange-300 gap-2">
              <PawPrint className="w-10 h-10" />
              <span className="text-xs text-zinc-400">Dodaj zdjęcie główne w kroku „Zdjęcia"</span>
            </div>
          )}
          <div className="absolute top-3 left-3 flex gap-1.5">
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${st.className}`}>{st.label}</span>
            {available > 0 && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-white/90 text-zinc-700">{available} dostępne</span>
            )}
          </div>
        </div>

        {/* Treść */}
        <div className="p-5 space-y-3">
          <div>
            <h3 className="text-xl font-bold text-zinc-900 leading-tight">{form.name || 'Nazwa miotu'}</h3>
            <p className="text-sm text-zinc-500">{form.breed || 'Rasa'}</p>
          </div>

          {parents && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Heart className="w-4 h-4 text-pink-400 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">Rodzice: {parents}</span>
            </div>
          )}

          {form.birth_date && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Calendar className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
              <span>Data urodzenia: {form.birth_date}</span>
            </div>
          )}

          {totalPuppies > 0 && (
            <div className="flex items-start gap-2 text-sm text-zinc-700">
              <Users className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <span>Szczenięta: {totalPuppies} ({parseInt(form.puppy_count_male) || 0}♂ · {parseInt(form.puppy_count_female) || 0}♀)</span>
            </div>
          )}

          {form.price && (
            <div className="flex items-start gap-2 text-sm text-emerald-700">
              <Tag className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <span className="font-semibold">{form.price} PLN / szczenię</span>
            </div>
          )}

          <p className="text-sm text-zinc-500 leading-relaxed line-clamp-3">
            {form.description || 'Tu pojawi się opis miotu — dodasz go w kroku „Opis".'}
          </p>

          <button className="w-full mt-1 h-10 rounded-xl bg-orange-500 text-white text-sm font-semibold">
            Zapytaj o szczeniaka
          </button>
        </div>

        {/* Miot = niezależna strona promocyjna */}
        <div className="bg-gray-50 border-t border-gray-100 p-3">
          <p className="text-gray-500 text-[10px] text-center mb-1">
            Po opublikowaniu oferta będzie widoczna w:
          </p>
          <p className="text-orange-600 text-[10px] text-center font-medium">
            {['Google', 'Bing', 'Mapy', 'Katalog APISAI', 'Facebook', 'Asystenci'].join(' · ')}
          </p>
        </div>
      </div>

      <p className="text-center text-xs text-zinc-400 mt-3">
        To samodzielna strona miotu — możesz ją promować niezależnie od hodowli.
      </p>
    </div>
  );
}
